import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Logger from 'observability/Logger';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { COOKIE_CATEGORY } from 'common/constants/cookieManagement';
import { GuestBrandedThemeWrapper } from 'common/compounds/GuestBrandedTheme';
import { prepareColors, prepareFonts } from 'common/compounds/GuestBrandedTheme/ApplyGuestBrandedTheme';
import CookieBanner from './CookieBanner/CookieBanner';
import { getBannerProps } from './CookieBanner/utils';
import { getUserCookieConsent, saveUserCookieConsent, deleteCookies } from '../cookieTools';

const FixedContainer = styled.div`
    position: fixed;
    bottom: 0;
    margin: 1rem;
    z-index: 9999;
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
    box-shadow: rgba(0, 0, 0, 0.25) 0px 4px 20px -2px;
`;

const prepareBrandedTheme = (appearance) => {
    const colors = prepareColors(appearance);
    const fonts = prepareFonts();

    // NOTE: We assume that fonts are already loaded in the guest side app.
    // If we notice any pages where the fonts aren’t loading, we can call the loadFonts() function
    // from ApplyGuestBrandedTheme.jsx

    return {
        '--guest-branded-background': colors.background,
        '--guest-branded-text': colors.text,
        '--guest-branded-text-hover': colors.textHover,
        '--guest-branded-action': colors.action,
        '--guest-branded-actionText': colors.actionText,
        '--guest-branded-actionHover': colors.actionHover,
        '--guest-branded-actionActive': colors.actionActive,
        '--guest-branded-font-body': fonts.body,
        '--guest-branded-font-heading': fonts.heading,
        '--guest-branded-font-subHeading': fonts.subHeading,
        '--guest-branded-font-button': fonts.button,
    };
};

const LOG = new Logger('GuestCookieManager');

const bootstrap = getBootstrap();
const eventId = bootstrap.get('event.id');
const cookieSettings = bootstrap.get('cookieSettings', {});

const GuestCookieManager = ({ onUserConsent }) => {
    const [originalCookieConsent] = useState(getUserCookieConsent(eventId));

    useEffect(() => {
        if (originalCookieConsent === null) {
            deleteCookies([COOKIE_CATEGORY.PERFORMANCE, COOKIE_CATEGORY.FUNCTIONALITY, COOKIE_CATEGORY.MARKETING]);
        }
    }, []);

    const getConsentChanges = (newUserConsent) => {
        const acceptedCategories = [];
        const declinedCategories = [];

        // If no original consent exists, treat all true values as newly accepted
        if (originalCookieConsent === null) {
            Object.keys(newUserConsent).forEach((category) => {
                if (newUserConsent[category]) {
                    acceptedCategories.push(category);
                }
            });
            return { acceptedCategories, declinedCategories };
        }

        // Compare new consent with original consent
        Object.keys(newUserConsent).forEach((category) => {
            if (newUserConsent[category] !== originalCookieConsent[category]) {
                if (newUserConsent[category]) {
                    acceptedCategories.push(category);
                } else {
                    declinedCategories.push(category);
                }
            }
        });

        return { acceptedCategories, declinedCategories };
    };

    const onConsentChange = async (newUserConsent) => {
        const { acceptedCategories, declinedCategories } = getConsentChanges(newUserConsent);

        saveUserCookieConsent(eventId, newUserConsent);

        if (acceptedCategories.length > 0) {
            // eslint-disable-next-line no-unused-expressions
            window.loadSpladminTrackers
                ? window.loadSpladminTrackers(acceptedCategories)
                : LOG.warn('loadSpladminTrackers is not available');

            // eslint-disable-next-line no-unused-expressions
            window.instantiateThirdPartyTrackingManager
                ? await window.instantiateThirdPartyTrackingManager(acceptedCategories)
                : LOG.warn('instantiateThirdPartyTrackingManager is not available');
        }
        if (declinedCategories.length > 0) {
            deleteCookies(declinedCategories);
            // Third-party cookies cannot be deleted, so
            // reload page to ensure all third-party cookies and scripts are properly reset
            window.location.reload();
        }
        onUserConsent();
    };

    // Prepare props for the CookieBanner component
    const bannerProps = getBannerProps(cookieSettings);

    const brandedTheme = prepareBrandedTheme(cookieSettings.cookieBannerDefaultColor);

    return (
        <FixedContainer>
            <GuestBrandedThemeWrapper theme={brandedTheme}>
                <CookieBanner
                    {...bannerProps}
                    cookieConsent={originalCookieConsent}
                    onAcceptAll={() => {
                        onConsentChange({
                            [COOKIE_CATEGORY.PERFORMANCE]: true,
                            [COOKIE_CATEGORY.FUNCTIONALITY]: true,
                            [COOKIE_CATEGORY.MARKETING]: true,
                        });
                    }}
                    onDeclineNonEssential={() => {
                        onConsentChange({
                            [COOKIE_CATEGORY.PERFORMANCE]: false,
                            [COOKIE_CATEGORY.FUNCTIONALITY]: false,
                            [COOKIE_CATEGORY.MARKETING]: false,
                        });
                    }}
                    onConfirmSelection={(consent) => onConsentChange(consent)}
                />
            </GuestBrandedThemeWrapper>
        </FixedContainer>
    );
};

GuestCookieManager.propTypes = {
    onUserConsent: PropTypes.func.isRequired,
};

export default GuestCookieManager;
